import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getSplitPeriodDuration, type IState } from "../helpers";
import { getConfigWorkdayMaximum } from "../hosCalculator";
import { isSplitCompleteBlockedByViolation } from "../splitSb/blockingViolations";
import { DRAINING, FROZEN, UNKNOWN_RATE, type TUnderlyingTrajectory } from "../exemptions/emergencyResponse";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type WorkdayConditions = {
    avail: number | undefined;
    workdayHasStarted: boolean;
    isInSleeperBerthNow: boolean;
    hasSbInstability: boolean;
    sbFreezeHolding: boolean;
    sbDrainingThroughSplit: boolean;
    sbFreezeAt: Date | undefined;
    sbFreezeValue: number | undefined;
    breakMidProgress: boolean;
    offDutyMinimum: number | undefined;
    configOffDutyMinimum: number | undefined;
    consecutiveOffStartTime: Date | undefined;
    workdayEnd: Date | undefined;
    isWtActive: boolean;
    consecutiveOffCountIncludesWt: boolean;
    wtExtendsWorkday: boolean;
};

export class WorkdayCalculator extends AvailabilityCalculator {
    /**
     * What the symmetric sleeper split is doing to `workday.end`, and therefore to the unfloored
     * value, from now on (MYG-139401). Four answers, and each is a shape the Emergency Response
     * floor can price:
     *
     * `holding` — the freeze is pinning end to the period the driver is still lying in, so the
     * period grows at +1 and the value is the constant `value`.
     * `at`/`value` with `holding` false — the value is draining now and becomes fixed at `value`
     * from `at`: either the freeze has not yet reached splitMinimum, or a second leg is running
     * and the pairing that ends it is already dated.
     * `draining` — a second leg whose pairing a violation is blocking, so nothing will lift the
     * value before the day boundary.
     * none of the above — not derivable here; the caller answers with a coverage gap.
     *
     * `holding` is tested as the pinning identity rather than a re-derivation of the conditions
     * behind it, so every guard inside evaluateSymmetricSplit is respected without being restated.
     * The other two cases have nothing pinned yet, so each mirrors the one guard that can still
     * cancel what it is predicting.
     */
    private symmetricSplitShape(state: IState): { holding: boolean; draining: boolean; at: Date | undefined; value: number | undefined } {
        const none = { holding: false, draining: false, at: undefined, value: undefined };
        const splitConfig = state.config.splitConfig;
        const periods = state.workday.splitSB?.periods;
        const start = state.workday.start;
        const end = state.workday.end;
        const workdayMaximum = getConfigWorkdayMaximum(state);
        if (splitConfig?.type !== "symmetric" || !state.workday.splitSB?.inSleeper || periods === undefined
                || start === undefined || end === undefined || workdayMaximum === undefined) {
            return none;
        }
        // collectSplitPeriodsSymmetric extends the LAST period, so that is the one the driver is
        // in; the freeze always targets periods[0]. With one period they are the same and the
        // freeze holds the value; with two they are not, and end is pinned to the closed leg.
        const open = periods[periods.length - 1];
        const freezeTarget = periods[0];
        if (open === undefined || open.toDate.getTime() !== state.nextDate.getTime()) {
            return none;
        }
        const freezeTargetLength = getSplitPeriodDuration(freezeTarget);

        if (periods.length === 1) {
            // setTime overwrites end outright, so the value the freeze will hold at is known
            // whether or not it has run, and whatever else moved end before is irrelevant.
            const value = start.getTime() + workdayMaximum - open.fromDate.getTime();
            if (freezeTargetLength >= splitConfig.splitMinimum) {
                return end.getTime() === start.getTime() + workdayMaximum + freezeTargetLength
                    ? { holding: true, draining: false, at: undefined, value }
                    : none;
            }
            const freezeBlocked = state.intermediateSplitConfig.violationPreventsNewFreeze
                && isSplitCompleteBlockedByViolation(state);
            return freezeBlocked
                ? none
                : { holding: false, draining: false, at: new Date(open.fromDate.getTime() + splitConfig.splitMinimum), value };
        }

        // Second leg. The value drains at −1 because end is pinned to a leg that has stopped
        // growing — confirm that pinning before predicting from it — and what ends the drain is
        // the pairing completing, which happens the moment the two legs together reach splitTotal.
        if (periods.length !== 2
                || freezeTargetLength < splitConfig.splitMinimum
                || end.getTime() !== start.getTime() + workdayMaximum + freezeTargetLength) {
            return none;
        }
        if (state.intermediateSplitConfig.violationPreventsSplitCompletion && isSplitCompleteBlockedByViolation(state)) {
            return { holding: false, draining: true, at: undefined, value: undefined };
        }
        // completeSplit rewinds the log loop and rebuilds the workday from the end of the first
        // leg, which lands the new window at firstLegEnd + workdayMaximum + secondLegLength — so
        // the value it settles at is firstLegEnd + workdayMaximum − secondLegStart, independent of
        // when it is asked. Not claimed for the four rulesets carrying workdayMaximumWhenSplitSB:
        // completion flips completedSplitSB, and with it which maximum getConfigWorkdayMaximum
        // returns, so `workdayMaximum` read here would not be the one the reset uses.
        if (state.config.workday?.workdayMaximumWhenSplitSB !== undefined) {
            return none;
        }
        return {
            holding: false,
            draining: false,
            at: new Date(open.fromDate.getTime() + splitConfig.splitTotal - freezeTargetLength),
            value: freezeTarget.toDate.getTime() + workdayMaximum - open.fromDate.getTime()
        };
    }

    private conditions(state: IState): WorkdayConditions {
        const avail = discreteAvailabilityFunctions.Workday(state, false);
        const workdayHasStarted = state.workday.hasStarted ?? false;
        const isInSleeperBerthNow = !!(state.workday.splitSB?.inSleeper || state.workday.propertySplitSB?.inSleeper);
        // Use <= so the break-at-boundary case (consecutiveOffStartTime === nextDate, break just
        // started) also emits workdayReset, preventing exhaustion from being predicted as the
        // winning event when a reset will fire earlier.
        const breakMidProgress = state.workday.consecutiveOffStartTime !== undefined
            && state.workday.consecutiveOffStartTime.getTime() <= state.nextDate.getTime();

        // Classify the split type to determine workday.end stability.
        //
        // Property splits (property, californiaProperty): workday.end is extended by
        // freezeSplitPeriodAsymmetric on every dummy-log step while the driver is in any off-duty
        // status. The anchor is unstable for the entire workday — guard at the config level.
        //
        // Passenger-style splits (passenger, canada, canadateam, canadanorthteam): workday.end
        // is extended only while inSleeper=true (evaluateSymmetricSplit in splitSb/symmetric.ts). The anchor
        // is stable while the driver is not in SB, so guard only when inSleeper or at the SB log
        // boundary (where inSleeper will flip to true before the predicted exhaustion is reached).
        const splitConfig = state.config.splitConfig;
        const isPropertySplitCapable = splitConfig?.type === "asymmetric";
        const isPassengerSplitCapable = splitConfig?.type === "symmetric";
        const hasSbInstability = isPropertySplitCapable
            || (isPassengerSplitCapable && (isInSleeperBerthNow || state.current?.status === "SB"));

        // MYG-139401: "unstable" is not the same as "unknown". evaluateSymmetricSplit pins
        // workday.end to start + workdayMaximum + periods[0] length on every step, so when
        // periods[0] is the period the driver is still lying in, it grows at +1 and avail
        // (= end − nextDate) collapses to the constant start + workdayMaximum − periodStart.
        // Before the period reaches splitMinimum the freeze has not started and the value is
        // still draining, but the instant it starts and the value it will hold at are both
        // already derivable — the two shapes underlyingTrajectory reports below.
        const sbSplit = this.symmetricSplitShape(state);


        // WT workday extension: extendWorkdayWithWt extends workday.end while driver is in WT,
        // keeping avail constant (rate 0). Detect this to return isConsuming = false.
        const wtExtendsWorkday = state.config.workday?.wtExtendsWorkday !== false;

        return {
            avail,
            workdayHasStarted,
            isInSleeperBerthNow,
            hasSbInstability,
            sbFreezeHolding: sbSplit.holding,
            sbDrainingThroughSplit: sbSplit.draining,
            sbFreezeAt: sbSplit.at,
            sbFreezeValue: sbSplit.value,
            breakMidProgress,
            offDutyMinimum: state.workday.offDutyMinimum,
            configOffDutyMinimum: state.config.workday?.offDutyMinimum,
            consecutiveOffStartTime: state.workday.consecutiveOffStartTime,
            workdayEnd: state.workday.end,
            isWtActive: state.current?.status === "WT",
            consecutiveOffCountIncludesWt: state.config.workday?.consecutiveOffCountIncludesWt !== false,
            wtExtendsWorkday,
        };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        const c = this.conditions(state);
        // When workday.end is not a stable anchor, the effective rate is 0 (or undefined).
        // WT extending workday: extendWorkdayWithWt extends workday.end at rate +1, canceling
        // the -1 drain rate, so effective rate is 0.
        const wtIsExtendingWorkday = c.wtExtendsWorkday && c.isWtActive && c.workdayHasStarted && c.workdayEnd !== undefined;
        return c.workdayHasStarted && !c.hasSbInstability && !wtIsExtendingWorkday;
    }

    /**
     * During a sleeper split `workday.end` is extended as the driver accumulates SB time, so the
     * rate is generally neither −1 nor 0 — the same condition behind the `UNKNOWN_splitSb`
     * coverage gap below. isConsuming() reports false there to mean "not draining at −1", not
     * "frozen".
     *
     * The exception is `sbFreezeHolding` (MYG-139401): once the freeze is pinning workday.end to
     * the growing period, avail is provably constant, so there `false` does mean exactly "frozen"
     * and the Emergency Response floor can name its own inflection at `dayBoundary − avail`. That
     * date is only the whole answer because `availabilityFactors()` emits `workdayReset` in the
     * same state — the reset can end the floor's governance first, and the envelope is what pairs
     * the two.
     */
    protected override underlyingTrajectory(state: IState): TUnderlyingTrajectory {
        const c = this.conditions(state);
        if (!c.hasSbInstability) return super.underlyingTrajectory(state);
        if (c.sbFreezeHolding) return FROZEN;
        if (c.sbDrainingThroughSplit) return DRAINING;
        if (c.sbFreezeAt !== undefined && c.sbFreezeValue !== undefined) {
            return { kind: "frozenFrom", at: c.sbFreezeAt, value: c.sbFreezeValue };
        }
        return UNKNOWN_RATE;
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (!c.workdayHasStarted || c.avail === undefined || !c.workdayEnd || c.hasSbInstability) return undefined;

        // WT extending workday: workday.end slides forward continuously (unstable anchor).
        const wtIsExtendingWorkday = c.wtExtendsWorkday && c.isWtActive && c.workdayEnd !== undefined;
        if (wtIsExtendingWorkday) return undefined;

        // Guard avail > 0: raw value may be ≤ 0 when already in violation.
        if (c.avail > 0) return c.workdayEnd;
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;  // no workday config in this ruleset

        if (!c.workdayHasStarted) {
            // Workday window not yet open. It starts on the next on-duty log (log-triggered),
            // which the outer system will detect. No time-based inflection to predict.
            return {};
        }

        // workday.end is not a stable anchor for this state — return a coverage gap.
        // Property splits: workday.end extends during any off-duty status via freezeSplitPeriodAsymmetric.
        // Passenger-style splits: workday.end extends while inSleeper or at the SB log boundary.
        if (c.hasSbInstability) {
            const factors: Record<string, TAvailabilityFactorEntry> = {
                UNKNOWN_splitSb: { factor: () => state.nextDate, label: "coverage gap" },
            };

            // MYG-139401: the ordinary workday reset fires at consecutiveOffStartTime +
            // offDutyMinimum during a split exactly as it does outside one, and it lifts avail to
            // the workday maximum. It is named wherever symmetricSplitShape could read the split
            // at all, because those are the states where another factor — the Emergency Response
            // floor — predicts past it: a workday that began on an earlier driver day resets
            // before the falling floor ever reaches the frozen value, and without this factor the
            // floor's date would be the calculator's last word and would be late. This does not
            // change the winner — the coverage gap above is earlier — so no non-exempt driver's
            // output moves.
            //
            // The gap above stays regardless. symmetricSplitShape can now date the pairing, but
            // that answers "when does this value stop moving", not the question UNKNOWN_splitSb
            // is standing in for: when workday.end becomes an anchor this calculator can predict
            // its own inflections from again.
            const splitRead = c.sbFreezeHolding || c.sbDrainingThroughSplit || c.sbFreezeAt !== undefined;
            const resetDate = splitRead ? this.workdayResetDate(state) : undefined;
            if (resetDate) {
                factors.workdayReset = { factor: () => resetDate, label: "workday reset" };
            }
            return factors;
        }

        if (c.breakMidProgress) {
            // A pending ExemptionReducedRest log that applies to this break resets the workday
            // when the break reaches the reduced minimum. Mirrors workdayDuty.ts.
            const reducedRestResetDate = this.reducedRestResetDate(state);
            if (reducedRestResetDate) {
                return {
                    workdayResetErr: { factor: () => reducedRestResetDate, label: "workday reset" },
                };
            }

            // Break in progress, no reduced rest applies. The workday resets at
            // consecutiveOffStartTime + effectiveOffDutyMinimum.
            //
            // WT guard: if status is WT and WT does not count toward consecutive off (consecutiveOffCountIncludesWt:false),
            // the consecutiveOffCount resets on every tick. With no new logs, the driver stays in WT
            // indefinitely and the workday never resets. The stale anchor is invalid — no factor.
            const resetDate = this.workdayResetDate(state);
            if (resetDate) {
                return {
                    workdayReset: { factor: () => resetDate, label: "workday reset" },
                };
            }
            return {};
        }

        // Workday started, no stable break anchor (on-duty, or at a log boundary).
        // workday.end (exhaustion) is the only upcoming time-based event and is reported
        // via nextExhaustionEvent — no availability factors here.
        return {};
    }
}

export const extractWorkdayConditions = (state: IState): WorkdayConditions =>
    new WorkdayCalculator().extractConditions(state) as WorkdayConditions;

export const computeWorkdayResult = (state: IState): TComputedAvailabilityResult | undefined =>
    new WorkdayCalculator().compute(state);
